import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl, history, useLocation } from 'umi';
import cx from 'classnames';
import moment from 'moment';
import { debounce } from 'lodash-es';
import {
  Layout,
  Justify,
  Icon,
  Segment,
} from 'tea-component';
import useData from './_hooks';
import ReportSourceLabel from '@/pages/intelligence/components/ReportSourceLabel';
import ReportCard from '@/pages/intelligence/components/ReportCard';
import Loading from '@/pages/intelligence/components/Loading';
import Tabs from '@intelligence/components/NewIconTabs';
import InsightsReportCard from '@intelligence/components/ReportCard/InsightsReportCard';
import SearchBar from '@intelligence/pages/InsightsReport/SearchBar';
import { pointInter } from '@/pages/home/utils/buriedPoint';
import { PageLogMes } from '@home/utils/buriedPointCfg';
import { fetchReportExcel } from '@intelligence/services/common';
import { pageReportTypeConfig } from '@intelligence/pages/InsightsReport/_config';
import { languageItem } from '@intelligence/pages/MobileDevice/Reports/_utils';
import { useStudioFullscreen } from '@home/pages/studio/components/StudioFullscreen/context';
import DataBrain from '@/pages/home/assets/svg/dataSource/databarin.svg';
import '@/pages/intelligence/assets/scss/BusinessReports/index.scss';
import '@/pages/intelligence/components/LazyReport/index.scss';
import './index.scss';

const { Content } = Layout;
const NEW_MARK_IDS_MAPPING_KEY = 'ReportsMarketReports';

const databrainIcon = <img src={DataBrain} className="dbi-source-label-new-icon" />;

const tabs = [
  {
    id: 'DataBrain',
    name: 'databrain',
    pagePoint: 'StudioReportsDataBrain',
    // label: <ReportSourceLabel sourceName="DataBrain" />,
    label: (
      <ReportSourceLabel
        // sourceName="Newzoo"
        needCustomName
        customName="DataBrain"
        needCustomIcon
        customIcon={databrainIcon}
      />
    ),
  },
  {
    id: 'Archived Market Report',
    name: 'archivedmarketreport',
    pagePoint: 'StudioReportsArchivedMarketReport',
    label: <ReportSourceLabel sourceName="Archived Market Report" />,
  },
  {
    id: 'Sensor Tower',
    name: 'sensortower',
    pagePoint: 'StudioReportsSensorTower',
    label: <ReportSourceLabel sourceName="Sensor Tower" />,
  },
  {
    id: 'data.ai',
    name: 'data.ai',
    pagePoint: 'StudioReportsDataAi',
    label: <ReportSourceLabel sourceName="data.ai" />,
  },
  {
    id: 'Stream Hatchet',
    name: 'streamhatchet',
    pagePoint: 'StudioReportsStreamHatchet',
    label: <ReportSourceLabel sourceName="Stream Hatchet" />,
  },
  {
    id: 'NPD',
    name: 'npd',
    pagePoint: 'StudioReportsNPD',
    label: <ReportSourceLabel sourceName="NPD" />,
  },
  {
    id: 'Statista',
    name: 'statista',
    pagePoint: 'StudioReportsStatista',
    label: <ReportSourceLabel sourceName="Statista" />,
  },
  {
    id: 'Famitsu',
    name: 'famitsu',
    pagePoint: 'StudioReportsFamitsu',
    label: <ReportSourceLabel sourceName="Famitsu" />,
  },
  {
    id: 'Twitter',
    name: 'twitter',
    pagePoint: 'StudioReportsTwitter',
    label: <ReportSourceLabel sourceName="Twitter" />,
  },
  {
    id: 'Fandom',
    name: 'fandom',
    pagePoint: 'StudioReportsFandom',
    label: <ReportSourceLabel sourceName="Fandom" />,
  },
  {
    id: 'Others',
    name: 'others',
    pagePoint: 'StudioReportsOthers',
    label: <ReportSourceLabel sourceName="Others" />,
  },
];

export default function StudioReport(props: {
  className: string;
  searchType: string;
  pageLogMes?: any;
  segmentList: any[];
  systemId: string;
  studioId: string;
  gameId: string;
  resData: any;
  rightOperateNode: React.ReactNode;
  onTabChange?: (activeId: {
    id: string;
    name: string;
  }) => void;
  reportLogFn: (paramsObj: pointInter) => void;
}) {
  const location = useLocation();
  const { formatMessage } = useIntl();
  const isStudioFullscreen = useStudioFullscreen()?.isFullscreen ?? false;
  const { className, segmentList, studioId, reportLogFn, rightOperateNode, onTabChange } = props || {};
  const {
    dataShow,
    showTabList,
    setActiveId,
    activeId,
    search,
    cacheRecentSearch,
    onSearch,
    onClear,
    onClickSearchRecent,
    deleteSearchRecent,
    lang,
    dataLoading,
    segmentValue,
    setSegmentValue,
    option,
    systemId,
    data,
    loading,
    loadingMore,
    noMore,
    lazyScrollRef,
  } = useData(props, tabs);

  // 大类切换，路由拼接和打点
  const onActive = (tab: any) => {
    onTabChange?.(tab);
    setActiveId(tab.id);
    history.replace(`${location?.pathname}?tab=${tab?.name}`);
    if (PageLogMes?.[tab?.pagePoint]) {
      reportLogFn?.(PageLogMes?.[tab?.pagePoint]);
    }
  };

  // segment切换事件
  const onSegmentChange = (value: string) => {
    // databrain来源tab切换打点
    if (activeId?.toLowerCase() === 'databrain') {
      const segment = segmentList?.find(v => v?.id?.toLowerCase() === value?.toLowerCase());
      segment?.pagePoint();
    }
    sessionStorage.setItem('__dbh_studio_reports_data_brain_tab__', value);
    setSegmentValue(value);
  };

  // 卡片excel下载
  const handleDownloadRun = async (id: string, name: string) => {
    if (!id) return;
    const res = await fetchReportExcel({ id, name, system: "studio_hub" });
    const blob = new Blob([res]);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = URL.createObjectURL(blob);
    // 保存下来的文件名
    a.download = name;
    document.body.appendChild(a);
    a.click();
    // 移除元素
    document.body.removeChild(a);
    return true;
  };
  const scrollBody = useRef(null);
  const [tabFixed, setTabFixed] = useState(false);
  const [scrollPageCount, setScrollPageCount] = useState(0);

  useLayoutEffect(() => {
    setTabFixed(false);
  }, [isStudioFullscreen]);

  // 监听页面滚动
  useEffect(() => {
    if (scrollBody?.current) {
      const handleScroll = debounce((e: any) => {
        if (isStudioFullscreen) {
          setTabFixed(false);
          return;
        }
        const { scrollTop } = e.srcElement;
        // 添加scroll值变化
        setScrollPageCount(scrollPageCount => scrollPageCount + 1);
        // 滚动判断距离 和定位条位置相关，开发调整
        if (scrollTop > 260) {
          setTabFixed(true);
        } else {
          setTabFixed(false);
        }
      }, 100);
      scrollBody.current?.addEventListener('scroll', handleScroll, false);
      return () =>
        scrollBody.current?.removeEventListener('scroll', handleScroll, false);
    }
  }, [isStudioFullscreen, scrollBody]);

  // 需要吸顶的元素
  const fixedDom = <div className="header-tab">
    <Tabs
      ceiling
      newMarkIdsMappingKey={NEW_MARK_IDS_MAPPING_KEY}
      tabs={showTabList}
      animated={false}
      activeId={activeId}
      onActive={onActive}
    />
    <SearchBar
      key={'studioReportSearchBarFixed'}
      preClassName={'dbi-insights-report'}
      searchValue={search}
      onSearch={onSearch}
      onClear={onClear}
      cacheRecentSearch={cacheRecentSearch}
      onClickSearchRecent={onClickSearchRecent}
      deleteSearchRecent={deleteSearchRecent}
      scrollPageCount={scrollPageCount}
    />
  </div>;

  return (
    <Content className={cx(className, "dbi-businessreports")} ref={scrollBody}>
      <Content.Header
        className="dbi-header contentHead-normal"
        title={formatMessage({ id: 'dbh_studio_report_title' })}
      >
        <div className="header-des"><span className='header-des-text'>{formatMessage({ id: 'dbh_studio_report_sub_title' })}</span></div>
        {fixedDom}
      </Content.Header>
      <Content.Body full ref={lazyScrollRef} className="dbi-market-report-full">
        {/* 吸顶内容 */}
        {!isStudioFullscreen && (
          <div className={`header-fixedtab ${tabFixed ? 'visible' : 'hide'}`}>
            {fixedDom}
          </div>
        )}
        {
          activeId?.toLowerCase() === "databrain" ? <Justify
            className="dbi-businessreports-justify"
            left={
              <div className='dbh-filter-row'>
                <Segment
                  className="segment-light"
                  value={segmentValue}
                  onChange={onSegmentChange}
                  options={option}
                />
                {rightOperateNode}
              </div>
            }
          /> : null
        }
        {loading ? (
          <div style={{ height: '100%', color: '#262832' }}>
            <Loading content={formatMessage({ id: 'common_loading' })} />
          </div>) : (
          <div>
            {data?.list?.map((item: any, index: number) => {
              if (item?.source?.toLowerCase() === 'databrain') {
                const shareBack = () => location?.query?.id || '';
                return <InsightsReportCard
                  key={index}
                  data={item}
                  systemId={systemId}
                  lang={lang}
                  isNew={!!moment(item?.update_time || item?.time).isAfter(moment().subtract(1, 'month'))}
                  shareBack={shareBack}
                  needPopover={true}
                  reportTypeConfig={pageReportTypeConfig.studioReport}
                />;
              }
              return (
                <ReportCard
                  hasPDF
                  key={index}
                  data={languageItem(item, lang)}
                  systemId={systemId}
                  studioId={studioId}
                  type={'Market'}
                  isNew={!!moment(item?.time).isAfter(moment().subtract(1, 'month'))}
                  handleDownloadRun={handleDownloadRun}
                  reportTypeConfig={pageReportTypeConfig.studioReport}
                  allowHtmlInLabels={true}
                />
              );
            })}
          </div>
        )}
        {loadingMore && (
          <div className="dbi-lazyreport-loading dbi-lazyreport-end">
            <Icon size="l" type="loading" />
            &nbsp;&nbsp;&nbsp;
            {formatMessage({ id: 'dbi_reports_loading_more' })}
          </div>
        )}
        {dataShow?.length <= 0 && noMore && !loading && !dataLoading && (
          <div className="dbi-reportcard-nodata dbi-lazy-nodata">
            {formatMessage({ id: 'dbi_reports_no_data' })}
          </div>
        )}
      </Content.Body>
    </Content>
  );
}
